import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Datetime from 'react-datetime';
import moment from 'moment';
// @material-ui/icons
import Clear from '@material-ui/icons/Close';
import Calendar from '@material-ui/icons/CalendarToday';
// @material-ui/core components
import DatepickerStyle from 'assets/jss/DatePicker';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import Button from 'components/UI/CustomButtons/Button';
import Popover from 'react-popover';

const checkValid = (current, fromToday, toToday, validGte, validLte) => {
  if (validGte) {
    if (fromToday)
      return current.isAfter(moment()) && current.isBefore(validGte);
    if (toToday)
      return current.isBefore(moment()) && current.isBefore(validGte);
    return current.isBefore(validGte);
  }
  if (validLte) {
    if (fromToday)
      return current.isAfter(moment()) && current.isAfter(validLte);
    if (toToday) return current.isBefore(moment()) && current.isAfter(validLte);
    return current.isAfter(validLte);
  }
  if (fromToday) return current.isAfter(moment());
  if (toToday) return current.isBefore(moment());
  return true;
};

/**
 * @description
 * Stateless component.
 *
 * @param {Object} props - Component props.
 */
export const DatePicker = props => {
  const {
    classes,
    onChange,
    defaultValue,
    value,
    title,
    required,
    fromToday,
    toToday,
    validGte,
    validLte,
    dateTime,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const anchor = useRef(null);
  return (
    <div className={classes.dateComponent} ref={anchor}>
      <div
        className={`dateBtn ${classes.dateBtn} ${
          value !== '' ? 'activeDate' : ''
        }`}
      >
        <div>
          <p className="title">{title}</p>
          <span className="day">
            {value !== '' ? moment(value).format('DD/MM/YYYY') : defaultValue}
          </span>
        </div>
        {value !== '' && !required ? (
          <Button
            className={classes.clearBtn}
            justIcon
            simple
            color="white"
            onClick={() => {
              onChange(null);
            }}
          >
            <Clear />
          </Button>
        ) : (
          <Button justIcon simple color={required ? 'white' : 'info'}>
            <Calendar />
          </Button>
        )}
      </div>
      <Popover
        isOpen={isOpen}
        onOuterAction={() => setIsOpen(false)}
        preferPlace="below"
        target={anchor.current}
        tipSize={0.01}
        style={{
          zIndex: 1000,
        }}
        body={
          <div
            style={{
              boxShadow: '0 10px 50px 0 rgb(0 0 0 / 20%)',
            }}
          >
            <Datetime
              input={false}
              timeFormat={dateTime}
              dateFormat="YYYY-MM-DD"
              onChange={date => {
                onChange(date);
                // This is the max value that can change
                // when hours or minutes were changed
                // 82800000 == 23 hours.
                setIsOpen(Math.abs(date - value) <= 82800000);
              }}
              value={value}
              id="datePickerDD"
              isValidDate={current =>
                checkValid(current, fromToday, toToday, validGte, validLte)
              }
            />
          </div>
        }
      >
        <button
          data-test-id="datePickerTrigger"
          type="button"
          className={classes.datePickerTrigger}
          onClick={() => setIsOpen(prevState => !prevState)}
        >
          open
        </button>
      </Popover>
    </div>
  );
};

/**
 * DatePicker PropTypes
 * @param classes - prop styles received from withStyles
 * @param title - prop date picker title
 * @param value - prop value received from parent
 * @param defaultValue - prop default value received from parent
 * @param required - if the date picker allow empty date
 * @param fromToday - only allow dates after today
 * @param toToday - only allow dates before today
 * @param validGte - prop to calculate valid dates
 * @param validLte - prop to calculate valid dates
 * @param dateTime - prop to know if component allows edit time
 * @callback onChange - callback called when selected date change
 */

DatePicker.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.instanceOf(moment),
  ]).isRequired,
  required: PropTypes.bool,
  fromToday: PropTypes.bool,
  toToday: PropTypes.bool,
  validGte: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.instanceOf(moment),
  ]),
  validLte: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.instanceOf(moment),
  ]),
  dateTime: PropTypes.bool,
};

DatePicker.defaultProps = {
  required: false,
  fromToday: false,
  toToday: false,
  validGte: null,
  validLte: null,
  dateTime: false,
};

export default withStyles(DatepickerStyle)(DatePicker);
